import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler } from 'hooks';
import { IInfinitePageResponse } from 'services/types';
import { apiClient } from 'utils';
import { getPlainText } from 'utils/global/plainText.utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  IChat,
  IChatMessage,
  IChatMessagesSearchParams,
  IChatThreadSearchParams,
  ICloseThread,
  ICreateMessage,
  ICreateThread,
  IReadMessage,
} from './types';

export const useGetThreads = (params?: IChatThreadSearchParams) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount,
  );

  return useInfiniteQuery<IChat[], DefaultError>({
    queryKey: ['chats-list', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Chat',
        params: {
          ...JSON.parse(JSON.stringify(params ?? {})),
          itemCount,
          page: pageParam,
        },
      });

      const data = await responseHandler<IInfinitePageResponse<IChat>>(
        response,
        {
          groupName: `useGetChats - ${pageParam}`,
        },
      );

      return infiniteHandler<IChat>(data);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetMessages = (params: IChatMessagesSearchParams) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount,
  );

  return useInfiniteQuery<IChatMessage[], DefaultError>({
    queryKey: ['messages-list', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Chat/Messages',
        params: {
          ...JSON.parse(JSON.stringify(params)),
          itemCount,
          page: pageParam,
        },
      });

      const data = await responseHandler<IInfinitePageResponse<IChatMessage>>(
        response,
        {
          groupName: `useGetMessagesList - ${pageParam}`,
        },
      );

      return infiniteHandler<IChatMessage>(data);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
    refetchOnMount: true,
  });
};

export const useGetThread = (id: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IChat, DefaultError>({
    queryKey: ['chat', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Chat/Thread/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetChat`,
        onNotFound: () => {},
      });
    },
  });
};

export const usePostThread = () => {
  const { responseHandler } = useResponseHandler();
  return useMutation<IChatMessage, DefaultError, ICreateThread>({
    mutationFn: async body => {
      const formData = new FormData();

      formData.append('RecipientId', body.recipientId);
      formData.append('Subject', body.subject);
      formData.append('Message', getPlainText(body.message));

      for (const attachment of body.attachments) {
        formData.append(`Attachments`, attachment);
      }

      const response = await apiClient({
        method: 'POST',
        url: 'Chat/CreateThread',
        body: formData,
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: `usePostThread`,
        onNotFound: () => {},
      });
    },
  });
};

export const usePostMessage = () => {
  const { responseHandler } = useResponseHandler();
  return useMutation<IChatMessage, DefaultError, ICreateMessage>({
    mutationFn: async body => {
      const formData = new FormData();

      formData.append('ThreadId', body.threadId);
      formData.append('RecipientId', body.recipientId);
      formData.append('Message', body.message);

      for (const attachment of body.attachments) {
        formData.append(`Attachments`, attachment);
      }

      const response = await apiClient({
        method: 'POST',
        url: 'Chat/SendMessage',
        body: formData,
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: `usePostMessage`,
        onNotFound: () => {},
      });
    },
  });
};
export const usePutReadMessage = () => {
  const { responseHandler } = useResponseHandler();
  return useMutation<IChat, DefaultError, IReadMessage>({
    mutationFn: async body => {
      const response = await apiClient({
        method: 'PUT',
        url: 'Chat/ReadMessage',
        body: body,
      });

      return responseHandler(response, {
        groupName: `usePutReadMessage`,
        onNotFound: () => {},
      });
    },
  });
};

export const usePutCloseThread = () => {
  const { responseHandler } = useResponseHandler();
  return useMutation<IChat, DefaultError, ICloseThread>({
    mutationFn: async body => {
      const response = await apiClient({
        method: 'PUT',
        url: 'Chat/CloseThread',
        body: body,
      });

      return responseHandler(response, {
        groupName: `usePutCloseThread`,
        onNotFound: () => {},
      });
    },
  });
};

export const useGetUnreadThreadsCount = (enabled = true) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<number, DefaultError>({
    queryKey: ['chat-unread-count'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Chat/UnreadCount`,
      });

      return responseHandler(response, {
        groupName: `useGetUnreadThreadsCount`,
        onNotFound: () => {},
      });
    },
    enabled,
  });
};
